import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse, PageQuery } from '../types/api.types';
import {
  CreateUserRequest,
  PatchUserRequest,
  User,
} from '../types/user.types';

@Injectable({ providedIn: 'root' })
export class UsersService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/auth/api/users`;

  /**
   * List users (always paginated). Note: `page` is 1-indexed.
   * Returns the raw `ApiCollection<User[]>` envelope.
   */
  list(query: PageQuery = {}): Observable<ApiPageResponse<User>> {
    let params = new HttpParams()
      .set('pageable', 'true')
      .set('page', String(query.page ?? 1))
      .set('size', String(query.size ?? 10))
      .set('isDeleted', String(query.isDeleted ?? false));
    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }
    return this.http.get<ApiPageResponse<User>>(this.base, { params });
  }

  get(id: number): Observable<User> {
    return this.http.get<User>(`${this.base}/${id}`);
  }

  /**
   * Résout une personne par identifiant naturel (username | email | téléphone)
   * — `GET /auth/api/users/resolve?identifier=`. **200** → le `User`, **404** →
   * introuvable (le caller propose alors de provisionner un nouveau compte).
   * Flow « rechercher d'abord » §13.A.
   */
  resolve(identifier: string): Observable<User> {
    const params = new HttpParams().set('identifier', identifier.trim());
    return this.http.get<User>(`${this.base}/resolve`, { params });
  }

  create(req: CreateUserRequest): Observable<User> {
    return this.http.post<User>(this.base, req);
  }

  patch(id: number, req: PatchUserRequest): Observable<User> {
    return this.http.patch<User>(`${this.base}/${id}`, req);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  /** Resets the user's password to a fresh server-generated value. */
  regeneratePassword(id: number): Observable<User> {
    return this.http.put<User>(`${this.base}/${id}/regenerate-password`, {});
  }
}
